import { RootState } from '../../../../../../../../init-store';
import { selectDebugMenuReport } from '../../../../../../../shared/features/room-agent/dtmf/debug-menu/selectors';
import { DebugMenuReport } from '../../../../../../../shared/features/room-agent/dtmf/debug-menu/types';
import { DTMF_COMMANDS } from '../../../../../../../shared/features/room-agent/dtmf/dtmf-commands';
import { FONT_FAMILY } from '../constants';
import { Area, Drawer } from '../types';
import { SCALE } from './constants';

const STATS_ITEM_FONT_SIZE = Math.round(24 * SCALE);
const STATS_ITEM_FONT = `${STATS_ITEM_FONT_SIZE}px ${FONT_FAMILY}`;
const STATS_ITEM_HEIGHT = Math.round(40 * SCALE);

const TELEMOST_HOST_HEIGHT = Math.round(40 * SCALE);

type StatItem = { name: string; getValue(report: DebugMenuReport): string | number | undefined };

const VIDEO_STATS: StatItem[] = [
    {
        name: 'room_agent_avg_draw_time',
        getValue(report: DebugMenuReport) {
            return report.drawingPerformance?.avgDrawTime;
        },
    },
    {
        name: 'room_agent_fps',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.frames_per_second;
        },
    },
    {
        name: 'frame_width',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.video_track?.frame_width;
        },
    },
    {
        name: 'frame_height',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.video_track?.frame_height;
        },
    },
    {
        name: 'packets_sent',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.packets_sent;
        },
    },
    {
        name: 'packets_lost',
        getValue(report: DebugMenuReport) {
            return report.rtc?.remote_inbound_rtp_video_streams[0]?.packets_lost;
        },
    },
    {
        name: 'frames_sent',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.video_track?.frames_sent;
        },
    },
    {
        name: 'frames_encoded',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.frames_encoded;
        },
    },
    {
        name: 'key_frames_encoded',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.key_frames_encoded;
        },
    },
    {
        name: 'total_packet_send_delay',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.total_packet_send_delay;
        },
    },
    {
        name: 'nack_count',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.nack_count;
        },
    },
    {
        name: 'pli_count',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.pli_count;
        },
    },
    {
        name: 'fir_count',
        getValue(report: DebugMenuReport) {
            return report.rtc?.outbound_rtp_video_stream.fir_count;
        },
    },
    {
        name: 'jitter',
        getValue(report: DebugMenuReport) {
            return report.rtc?.remote_inbound_rtp_video_streams[0]?.jitter;
        },
    },
    {
        name: 'round_trip_time',
        getValue(report: DebugMenuReport) {
            return report.rtc?.remote_inbound_rtp_video_streams[0]?.round_trip_time;
        },
    },
];

const VIDEO_STATS_MARGIN_TOP = Math.round(20 * SCALE);
const VIDEO_STATS_HEIGHT = VIDEO_STATS_MARGIN_TOP + VIDEO_STATS.length * STATS_ITEM_HEIGHT;

const COMMAND_DESCRIPTIONS = [
    `${DTMF_COMMANDS.debug.drawConference.toggle} - Включить/выключить рендер конференции`,
    `${DTMF_COMMANDS.debug.halt} - Замена рум агента`,
    `${DTMF_COMMANDS.common.close} - Закрыть`,
];
const COMMAND_DESCRIPTION_ITEM_HEIGHT = Math.round(40 * SCALE);
const COMMAND_DESCRIPTIONS_MARGIN_TOP = Math.round(10 * SCALE);
const COMMAND_DESCRIPTIONS_HEIGHT =
    COMMAND_DESCRIPTIONS_MARGIN_TOP + COMMAND_DESCRIPTIONS.length * COMMAND_DESCRIPTION_ITEM_HEIGHT;

export const STATS_DRAWER_WIDTH = Math.round(600 * SCALE);
export const STATS_DRAWER_HEIGHT = TELEMOST_HOST_HEIGHT + VIDEO_STATS_HEIGHT + COMMAND_DESCRIPTIONS_HEIGHT;

export class StatsDrawer implements Drawer {
    private fullArea: Area;
    private telemostHostArea: Area;
    private videoStatsArea: Area;
    private commandsDescriptionArea: Area;

    constructor(fullArea: Area) {
        this.fullArea = fullArea;
        this.telemostHostArea = {
            x: this.fullArea.x,
            y: this.fullArea.y,
            width: this.fullArea.width,
            height: TELEMOST_HOST_HEIGHT,
        };
        this.videoStatsArea = {
            x: this.fullArea.x,
            y: this.telemostHostArea.y + this.telemostHostArea.height + VIDEO_STATS_MARGIN_TOP,
            width: this.fullArea.width,
            height: VIDEO_STATS_HEIGHT,
        };
        this.commandsDescriptionArea = {
            x: this.fullArea.x,
            y: this.videoStatsArea.y + this.videoStatsArea.height + COMMAND_DESCRIPTIONS_MARGIN_TOP,
            width: this.fullArea.width,
            height: COMMAND_DESCRIPTIONS_HEIGHT,
        };
    }

    drawListItem(ctx: CanvasRenderingContext2D, area: Area, text: string, index: number) {
        ctx.textBaseline = 'top';
        ctx.font = STATS_ITEM_FONT;
        ctx.fillStyle = '#fff';
        ctx.fillText(text, area.x, area.y + index * STATS_ITEM_HEIGHT);
    }

    drawTelemostHost(ctx: CanvasRenderingContext2D) {
        this.drawListItem(ctx, this.fullArea, window.location.hostname, 0);
    }

    drawOutboundVideoStats(ctx: CanvasRenderingContext2D, report: DebugMenuReport) {
        for (let i = 0; i < VIDEO_STATS.length; i++) {
            const item = VIDEO_STATS[i];

            this.drawListItem(ctx, this.videoStatsArea, `${item.name}: ${item.getValue(report) ?? 'n/a'}`, i);
        }
    }

    drawCommandDescriptions(ctx: CanvasRenderingContext2D) {
        for (let i = 0; i < COMMAND_DESCRIPTIONS.length; i++) {
            this.drawListItem(ctx, this.commandsDescriptionArea, COMMAND_DESCRIPTIONS[i], i);
        }
    }

    draw(ctx: CanvasRenderingContext2D, state: RootState) {
        this.drawTelemostHost(ctx);
        this.drawCommandDescriptions(ctx);

        const report = selectDebugMenuReport(state);

        if (!report) {
            return;
        }

        this.drawOutboundVideoStats(ctx, report);
    }
}
